// Opt-in only. Fixed domains, fixed typed-array rings, no DOM or allocations in
// record(). CPU graphs use 100 ms wall-clock buckets, including idle buckets.
export const presentationDomains = [
    ['camera', 'Camera & input', 'Per rendered frame · positions published at 20 Hz'],
    ['particles', 'Particles & gas / flames', '128 systems · 256 nodes · 16,384 particles · 256 traces/frame'],
    ['styles', 'Map lightstyles', 'Per frame · map lighting animation'],
    ['visibility', 'Map visibility & asset scheduling', 'Per frame · shared by Live and Explorer'],
    ['transforms', 'Scene transforms & loading indicators', 'Once per frame · shared by all scene and water passes'],
    ['water', 'Water render submission', 'CPU submission only; GPU time is not measured'],
    ['draw', 'Scene render submission', 'Main sky, world and composite · excludes water captures · CPU only'],
    ['overlay', 'Performance overlay', 'Graphs ≤10 Hz · text ≤4 Hz · fixed histories'],
    ['liveFrame', 'Live simulation total', 'Inclusive total · overlaps detailed simulation, effects and audio rows'],
    ['movement', 'Movement & parenting', '200 ms interpolation · 100 ms extrapolation'],
    ['animation', 'Skeletal animation & bounds', 'Per frame · 32 network history samples'],
    ['sprites', 'Sprites & trails', '256 trail points'],
    ['sprays', 'Spray animation', 'One retained spray/player slot'],
    ['physics', 'Ragdolls, gibs & karts', '6 substeps · 100 ms catch-up · 256 traces/frame'],
    ['lighting', 'Lighting & ambient samples', '32 dynamic lights · 8 ambient samples/frame · 0.5 ms budget · 4 Hz/entity'],
    ['models', 'Model setup', '6 concurrent Live model loads · synchronous setup only'],
    ['sound', 'Sound positioning & announcer', '128 voices · 4 loads · 32 MiB/file'],
    ['voice', 'Voice handoff & HUD', '50 Hz packets · 60 ms startup · 200 ms buffer · 8 decode requests'],
    ['hud', 'Chat, scoreboard & target ID', 'Scoreboard clock 4 Hz · voice expiry 10 Hz · 64 chat lines'],
    ['apply', 'Apply incoming world state', 'One worker delivery in flight · includes synchronous asset/effect setup'],
    ['liveVisibility', 'Live entity visibility', 'Animated bounds and brush PVS · excludes map visibility'],
    ['liveCallbacks', 'Live page callbacks', 'Camera publication, target ID, annotations and rendered-frame callbacks'],
];

export class PresentationMetrics {
    constructor(now = performance.now(), capacity = 120) {
        this.capacity = capacity;
        this.series = new Map(presentationDomains.map(([key]) => [key, {
            cpu: new Float32Array(capacity), calls: new Uint32Array(capacity),
            durations: new Float32Array(240), sorted: new Float32Array(240), cursor: 0, count: 0,
        }]));
        this.reset(now);
    }
    reset(now = performance.now()) {
        this.started = now; this.bucket = Math.floor(now / 100);
        for (const s of this.series.values()) {
            s.cpu.fill(0); s.calls.fill(0); s.cursor = s.count = 0;
        }
    }
    advance(now) {
        const bucket = Math.floor(now / 100);
        if (bucket <= this.bucket) return;
        const first = Math.max(this.bucket + 1, bucket - this.capacity + 1);
        for (const s of this.series.values()) for (let b = first; b <= bucket; b++) {
            s.cpu[b % this.capacity] = 0; s.calls[b % this.capacity] = 0;
        }
        this.bucket = bucket;
    }
    record(key, milliseconds, now = performance.now()) {
        if (!Number.isFinite(milliseconds) || milliseconds < 0) return;
        const s = this.series.get(key); if (!s) return;
        this.advance(now);
        const at = this.bucket % this.capacity;
        s.cpu[at] += milliseconds; s.calls[at]++;
        s.durations[s.cursor] = milliseconds; s.cursor = (s.cursor + 1) % s.durations.length;
        s.count = Math.min(s.count + 1, s.durations.length);
    }
    summary(key, now = performance.now()) {
        this.advance(now);
        const s = this.series.get(key);
        let cpu = 0, calls = 0, sum = 0;
        for (let i = 0; i < this.capacity; i++) { cpu += s.cpu[i]; calls += s.calls[i]; }
        for (let i = 0; i < s.count; i++) { sum += s.durations[i]; s.sorted[i] = s.durations[i]; }
        const sorted = s.sorted.subarray(0, s.count); sorted.sort();
        const elapsed = Math.max(1, now - Math.max(this.started, (this.bucket - this.capacity + 1) * 100));
        return { calls, hz: calls * 1000 / elapsed, percent: cpu * 100 / elapsed,
            mean: s.count ? sum / s.count : null, p95: s.count ? sorted[Math.ceil(s.count * .95) - 1] : null,
            max: s.count ? sorted[s.count - 1] : null };
    }
}
